// Per-weekday work hours: the shape of Organization.work_hours and
// BookingConfiguration.available_hours, {"mon": {"open": "08:00", "close": "16:00", "closed": false}, ...}.
// Weekday indexes follow Python (0 = Monday … 6 = Sunday), like the API's work_schedule.weekdays.

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Translation keys of the short day names ("Ma", "Mon", "Mån")
export const WEEKDAY_SHORT_LABEL_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const DEFAULT_OPEN = '08:00';
export const DEFAULT_CLOSE = '16:00';

// Monday-Friday 08-16, the organization default
export const DEFAULT_WORK_HOURS = Object.freeze(
  WEEKDAY_KEYS.reduce((acc, key, index) => {
    acc[key] = { open: DEFAULT_OPEN, close: DEFAULT_CLOSE, closed: index >= 5 };
    return acc;
  }, {})
);

// The old single weekday range + one period (wrap-around allowed) -> per-weekday hours
export const legacyToWorkHours = (weekStart = 0, weekEnd = 4, dayStart = DEFAULT_OPEN, dayEnd = DEFAULT_CLOSE) => {
  const open = new Set();
  for (let day = weekStart; ; day = (day + 1) % 7) {
    open.add(day);
    if (day === weekEnd || open.size === 7) break;
  }
  return WEEKDAY_KEYS.reduce((acc, key, index) => {
    acc[key] = { open: String(dayStart).slice(0, 5), close: String(dayEnd).slice(0, 5), closed: !open.has(index) };
    return acc;
  }, {});
};

// "HH:MM[:SS]" -> Date today at that time (for MUI TimePicker)
export const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes || 0, 0, 0);
  return date;
};

// Date -> "HH:MM"
export const dateToTimeString = (date) => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// "HH:MM" -> minutes since midnight
export const timeToMinutes = (timeStr) => {
  const [hours, minutes] = String(timeStr).split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

// JS Date -> Python weekday index (0 = Monday)
export const weekdayIndexOf = (date) => (new Date(date).getDay() + 6) % 7;

export const weekdayKeyForDate = (date) => WEEKDAY_KEYS[weekdayIndexOf(date)];

// {start, end} ("HH:MM") of a weekday index, or null when closed / unknown
export const hoursForWeekday = (workHours, index) => {
  const day = workHours?.[WEEKDAY_KEYS[index]];
  if (!day || day.closed || !day.open || !day.close) return null;
  return { start: day.open.slice(0, 5), end: day.close.slice(0, 5) };
};

export const hoursForDate = (workHours, date) => hoursForWeekday(workHours, weekdayIndexOf(date));

// Open weekday indexes, ascending
export const openWeekdays = (workHours) =>
  WEEKDAY_KEYS.map((_, index) => index).filter((index) => hoursForWeekday(workHours, index));

// {start, end} of the earliest open and latest close over the open days, or null when none is open
export const boundsOf = (workHours) => {
  const hours = WEEKDAY_KEYS.map((_, index) => hoursForWeekday(workHours, index)).filter(Boolean);
  if (hours.length === 0) return null;
  return {
    start: hours.reduce((min, h) => (h.start < min ? h.start : min), hours[0].start),
    end: hours.reduce((max, h) => (h.end > max ? h.end : max), hours[0].end),
  };
};

// Any value -> all seven days. No value at all is the default week (Mon-Fri 08-16); a day missing
// from a partial value is closed, as the server reads it (booking hours saved without some day must
// not open online booking on that day when edited).
export const normalizeWorkHours = (workHours) =>
  WEEKDAY_KEYS.reduce((acc, key) => {
    const day = workHours?.[key];
    if (day) {
      acc[key] = {
        open: (day.open || DEFAULT_OPEN).slice(0, 5),
        close: (day.close || DEFAULT_CLOSE).slice(0, 5),
        closed: !!day.closed,
      };
    } else {
      acc[key] = workHours ? { ...DEFAULT_WORK_HOURS[key], closed: true } : { ...DEFAULT_WORK_HOURS[key] };
    }
    return acc;
  }, {});

// One day's open/close copied to every day. Which days are closed does not change; a closed day
// gets the times too, so reopening it later starts from them.
export const copyHoursToAllDays = (workHours, fromKey) => {
  const hours = normalizeWorkHours(workHours);
  const { open, close } = hours[fromKey];
  return WEEKDAY_KEYS.reduce((acc, key) => {
    acc[key] = { ...hours[key], open, close };
    return acc;
  }, {});
};

// First problem of a value as a translation key, or null. Mirrors validate_work_hours on the server.
export const workHoursError = (workHours) => {
  const hours = WEEKDAY_KEYS.map((_, index) => hoursForWeekday(workHours, index)).filter(Boolean);
  if (hours.length === 0) return 'workHours.errorNoOpenDay';
  if (hours.some((h) => timeToMinutes(h.start) >= timeToMinutes(h.end))) return 'workHours.errorEndBeforeStart';
  return null;
};

// "Ma-To 08:00-17:00, Pe 08:00-14:00": consecutive days with the same hours grouped, closed days left out
export const formatWorkHoursSummary = (workHours, t) => {
  const groups = [];
  WEEKDAY_KEYS.forEach((_, index) => {
    const hours = hoursForWeekday(workHours, index);
    const last = groups[groups.length - 1];
    if (hours && last && last.to === index - 1 && last.start === hours.start && last.end === hours.end) {
      last.to = index;
    } else if (hours) {
      groups.push({ from: index, to: index, start: hours.start, end: hours.end });
    }
  });
  return groups
    .map(({ from, to, start, end }) => {
      const days = from === to
        ? t(WEEKDAY_SHORT_LABEL_KEYS[from])
        : `${t(WEEKDAY_SHORT_LABEL_KEYS[from])}-${t(WEEKDAY_SHORT_LABEL_KEYS[to])}`;
      return `${days} ${start}-${end}`;
    })
    .join(', ');
};
